import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Input,
  Button,
  Table,
  UncontrolledDropdown,
  UncontrolledAlert,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalBody,
  ModalFooter
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import { usePocketBase } from "../../../PocketBaseContext";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DeleteModal from "../../../Components/Common/DeleteModal";
import debounce from 'lodash.debounce';

const ReformProductList = () => {
  document.title = "Products | Reform Panel";
  
  const pb = usePocketBase();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    supplier: "",
  });
  const [sortField, setSortField] = useState("created");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [alert, setAlert] = useState({ show: false, color: "", message: "" });

  useEffect(() => {
    fetchProducts();
    fetchSuppliers();
    fetchCategories();
  }, []);

  useEffect(() => {
    debouncedFetchProducts();
  }, [filters, sortField, sortOrder]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      let filter = "";
      if (filters.search) {
        filter += `(product_name ~ "${filters.search}" || product_code ~ "${filters.search}")`;
      }
      if (filters.category) {
        filter += filter ? ` && ` : '';
        filter += `category_relation.id ?~ "${filters.category}"`;
      }
      if (filters.supplier) {
        filter += filter ? ` && ` : '';
        filter += `supplier = "${filters.supplier}"`;
      }
      
      const resultList = await pb.collection('products').getList(1, 50, {
        sort: `${sortOrder === "desc" ? "-" : ""}${sortField}`,
        expand: 'supplier,category_relation',
        filter: filter,
      });
      console.log("Fetched products:", resultList.items);
      setProducts(resultList.items);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching products:", error);
      setLoading(false);
    }
  };

  const debouncedFetchProducts = useCallback(debounce(fetchProducts, 300), [filters, sortField, sortOrder]);

  const fetchSuppliers = async () => {
    try {
      const resultList = await pb.collection('supplier').getList(1, 50, {
        sort: 'supplier_name',
      });
      console.log("Fetched suppliers:", resultList.items);
      setSuppliers(resultList.items);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const resultList = await pb.collection('category').getList(1, 50, {
        sort: 'category_type',
      });
      console.log("Fetched categories:", resultList.items);
      setCategories(resultList.items);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const handleDeleteProduct = async () => {
    if (productToDelete) {
      try {
        await pb.collection('products').delete(productToDelete.id);
        setDeleteModal(false);
        fetchProducts();
      } catch (error) {
        console.error("Error deleting product:", error);
      }
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleEditSelected = () => {
    if (selectedProducts.length === 1) {
      navigate(`/reform-products/detail/${selectedProducts[0]}`);
    } else if (selectedProducts.length > 1) {
      setAlert({
        show: true,
        color: "warning",
        message: "Lütfen düzenlemek için sadece bir ürün seçin."
      });
    } else {
      setAlert({
        show: true,
        color: "info",
        message: "Lütfen düzenlemek için bir ürün seçin."
      });
    }
  };

  const handleSelectAllProducts = (e) => {
    if (e.target.checked) {
      setSelectedProducts(products.map(product => product.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedProducts.map(id => pb.collection('products').delete(id)));
      fetchProducts();
      setSelectedProducts([]);
      setBulkDeleteModal(false);
    } catch (error) {
      console.error("Error deleting products:", error);
    }
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleRowClick = (productId) => {
    navigate(`/reform-products/detail/${productId}`);
  };

  const closeAlert = () => {
    setAlert({ show: false, color: "", message: "" });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Ürün Listesi" pageTitle="Ürünler" />
        {alert.show && (
          <UncontrolledAlert color={alert.color} toggle={closeAlert}>
            {alert.message}
          </UncontrolledAlert>
        )}
        <Row>
          <Col lg={3}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">Filters</h4>
              </CardHeader>
              <CardBody>
                <div>
                  <Input
                    type="text"
                    className="form-control"
                    placeholder="Search Products..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange("search", e.target.value)}
                  />
                </div>
                <div className="mt-3">
                  <Select
                    value={categories.find(cat => cat.id === filters.category)}
                    onChange={(selected) => handleFilterChange("category", selected ? selected.value : "")}
                    options={[
                      { value: "", label: "All Categories" },
                      ...categories.map(category => ({
                        value: category.id,
                        label: category.category_type
                      }))
                    ]}
                    placeholder="Select Category"
                    isClearable
                  />
                </div>
                <div className="mt-3">
                  <Select
                    value={suppliers.find(sup => sup.id === filters.supplier)}
                    onChange={(selected) => handleFilterChange("supplier", selected ? selected.value : "")}
                    options={[
                      { value: "", label: "All Suppliers" },
                      ...suppliers.map(supplier => ({
                        value: supplier.id,
                        label: supplier.supplier_name
                      }))
                    ]}
                    placeholder="Select Supplier"
                    isClearable
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col lg={9}>
            <Card>
              <CardBody>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <div className="search-box">
                      <Input
                        type="text"
                        className="form-control search"
                        placeholder="Search Products..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange("search", e.target.value)}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                  </div>
                  <div className="col-md-8 text-md-end">
                    {selectedProducts.length > 0 && (
                      <>
                        <Button color="danger" className="me-2" onClick={() => setBulkDeleteModal(true)}>
                          <i className="ri-delete-bin-line align-bottom me-1"></i> Delete Selected
                        </Button>
                        <Button color="primary" className="me-2" onClick={handleEditSelected}>
                          <i className="ri-edit-line align-bottom me-1"></i> Edit Selected
                        </Button>
                      </>
                    )}
                    <Link to="/reform-products/add" className="btn btn-success">
                      <i className="ri-add-line align-bottom me-1"></i> Add Product
                    </Link>
                  </div>
                </div>
                <div className="table-responsive">
                  <Table className="table-hover table-nowrap mb-0 align-middle">
                    <thead className="table-light text-muted">
                      <tr>
                        <th scope="col" style={{ width: "46px" }}>
                          <div className="form-check">
                            <Input 
                              className="form-check-input" 
                              type="checkbox" 
                              id="checkAll" 
                              onChange={handleSelectAllProducts}
                              checked={selectedProducts.length === products.length}
                            />
                          </div>
                        </th>
                        <th>Picture</th>
                        <th onClick={() => handleSort("product_name")}>Product Name</th>
                        <th onClick={() => handleSort("product_code")}>Product Code</th>
                        <th onClick={() => handleSort("stock_quantity")}>Stock</th>
                        <th onClick={() => handleSort("kdv_vat_rate")}>VAT Rate</th>
                        <th onClick={() => handleSort("currency")}>Currency</th>
                        <th onClick={() => handleSort("purchase_price_excl_vat")}>Purchase Price (Excl. VAT)</th>
                        <th onClick={() => handleSort("purchase_price_incl_vat")}>Purchase Price (Incl. VAT)</th>
                        <th onClick={() => handleSort("sales_price_excl_vat")}>Sales Price (Excl. VAT)</th>
                        <th onClick={() => handleSort("sales_price_incl_vat")}>Sales Price (Incl. VAT)</th>
                        <th>Supplier</th>
                        <th>Category</th>
                        <th onClick={() => handleSort("created")}>Created</th>
                        <th onClick={() => handleSort("updated")}>Updated</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product) => (
                        <tr key={product.id} onClick={() => handleRowClick(product.id)} style={{cursor: 'pointer'}}>
                          <th scope="row" onClick={(e) => e.stopPropagation()}>
                            <div className="form-check">
                              <Input 
                                className="form-check-input" 
                                type="checkbox" 
                                onChange={() => handleSelectProduct(product.id)}
                                checked={selectedProducts.includes(product.id)}
                              />
                            </div>
                          </th>
                          <td>
                            <img src={pb.getFileUrl(product, product.picture)} alt="" width="50" height="50" />
                          </td>
                          <td>{product.product_name}</td>
                          <td>{product.product_code}</td>
                          <td>{product.stock_quantity}</td>
                          <td>{product.kdv_vat_rate}%</td>
                          <td>{product.currency}</td>
                          <td>{product.purchase_price_excl_vat}</td>
                          <td>{product.purchase_price_incl_vat}</td>
                          <td>{product.sales_price_excl_vat}</td>
                          <td>{product.sales_price_incl_vat}</td>
                          <td>{product.expand?.supplier?.supplier_name}</td>
                          <td>{product.expand?.category_relation?.map(cat => cat.category_type).join(", ")}</td>
                          <td>{new Date(product.created).toLocaleString()}</td>
                          <td>{new Date(product.updated).toLocaleString()}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <UncontrolledDropdown>
                              <DropdownToggle tag="button" className="btn btn-soft-secondary btn-sm">
                                <i className="ri-more-fill" />
                              </DropdownToggle>
                              <DropdownMenu className="dropdown-menu-end">
                                <DropdownItem tag={Link} to={`/reform-products/detail/${product.id}`}>
                                  <i className="ri-eye-fill align-bottom me-2 text-muted"></i> View
                                </DropdownItem>
                                <DropdownItem tag={Link} to={`/reform-products/detail/${product.id}`}>
                                  <i className="ri-pencil-fill align-bottom me-2 text-muted"></i> Edit
                                </DropdownItem>
                                <DropdownItem divider />
                                <DropdownItem href="#" onClick={(e) => {
                                  e.preventDefault();
                                  setProductToDelete(product);
                                  setDeleteModal(true);
                                }}>
                                  <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i> Delete
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="row align-items-center mt-2 g-3 text-center text-sm-start">
                  <div className="col-sm">
                  <div className="text-muted">Showing <span className="fw-semibold">10</span> of <span className="fw-semibold">{products.length}</span> Results</div>
                  </div>
                  <div className="col-sm-auto">
                    <Pagination className="pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
                      <PaginationItem disabled><PaginationLink href="#">Previous</PaginationLink></PaginationItem>
                      <PaginationItem active><PaginationLink href="#">1</PaginationLink></PaginationItem>
                      <PaginationItem disabled><PaginationLink href="#">Next</PaginationLink></PaginationItem>
                    </Pagination>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteProduct}
        onCloseClick={() => setDeleteModal(false)}
      />
      <Modal isOpen={bulkDeleteModal} toggle={() => setBulkDeleteModal(!bulkDeleteModal)}>
        <ModalBody>
          Seçili ürünleri silmek istediğinizden emin misiniz?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setBulkDeleteModal(false)}>İptal</Button>
          <Button color="danger" onClick={handleBulkDelete}>Sil</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ReformProductList;